import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/apiConfig';

/* Super admin — payments across the platform: gateway accounts (Gympe's own
   and gyms' own), fee plans, each tier's default fee plan, and turning accrued
   fees into Gympe's monthly invoices. Credentials are write-only.

   Gateways differ in what an account needs beyond its credentials. Each
   provider says so itself: its config fields (PhonePe's merchant domain and
   IP), whether it can split a settlement (Razorpay Route can; PhonePe can't),
   and whether its webhooks are registered over its API (PhonePe) rather than
   pasted into its dashboard with a shared secret (Razorpay). */

interface ConfigField { key: string; label: string; fallback?: string; required: boolean }
interface Provider {
  key: string;
  label: string;
  capabilities: Record<string, boolean>;
  credentialFields: { key: string; label: string; secret: boolean }[];
  configFields: ConfigField[];
  /** Webhooks are registered over the gateway's API, from the account row. */
  webhooksManaged: boolean;
}
interface Account {
  id: string;
  provider: string;
  providerLabel: string;
  owner: 'platform' | 'gym';
  ownerGymId: string | null;
  settlement: 'direct' | 'split';
  mode: 'direct' | 'collect';
  label: string;
  environment: 'test' | 'live';
  status: 'active' | 'disabled';
  config: Record<string, unknown> | null;
  credentials: Record<string, string>;
  configFields: ConfigField[];
  webhooksManaged: boolean;
  hasWebhookSecret: boolean;
  webhookPath: string;
}
interface WebhookStatus {
  managed: boolean;
  expectedUrl: string | null;
  registered?: boolean;
  webhooks: { id: string; url: string; enabled: boolean }[];
}
interface Registered { id: string; url: string; username: string; password: string; events: string[] }

/**
 * How an account's money moves, in words. Ownership says whose credentials;
 * the mode says where it settles — a Gympe account either collects and pays
 * gyms out (split) or, as a partner (PhonePe TSP), settles straight to each
 * gym's own merchant ID.
 */
export function accountKind(a: { owner: 'platform' | 'gym'; mode?: 'direct' | 'collect' }) {
  if (a.owner === 'gym') return { badge: 'Gym direct', detail: "Gym's own account, money goes direct", tone: 'bg-emerald-50 text-emerald-700' };
  if (a.mode === 'direct') {
    return { badge: 'Gympe partner · settles to gym', detail: "Gympe's partner account, settles straight to the gym's own merchant ID", tone: 'bg-sky-50 text-sky-700' };
  }
  return { badge: 'Gympe collect', detail: 'Gympe collects, pays out to gym', tone: 'bg-indigo-50 text-indigo-700' };
}

/** The config to send: blanks dropped, so a field with a fallback uses it. */
const cleanConfig = (values: Record<string, string>) =>
  Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));
interface FeePlan {
  id: string;
  name: string;
  percentBps: number;
  fixedPaise: number;
  minPaise: number | null;
  maxPaise: number | null;
  isActive: boolean;
}
interface Tier { id: string; name: string; feePlanId?: string | null }
interface BusinessSetting { id: string; businessName: string }

export function useSaasApi(token: string) {
  return useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const res = await fetch(`${API_BASE_URL}/api/saas${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || 'Request failed');
    return json.data as T;
  }, [token]);
}

export const feeLabel = (p: FeePlan) =>
  [p.percentBps ? `${(p.percentBps / 100).toFixed(2)}%` : null, p.fixedPaise ? `₹${(p.fixedPaise / 100).toFixed(2)}` : null]
    .filter(Boolean).join(' + ') || 'No fee';

const input = 'w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500';
const card = 'bg-white rounded-xl border border-gray-200 p-5';

export default function PaymentsTab({ token }: { token: string }) {
  const api = useSaasApi(token);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [feePlans, setFeePlans] = useState<FeePlan[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [settings, setSettings] = useState<BusinessSetting[]>([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a, f] = await Promise.all([
        api<Provider[]>('/payments/providers'),
        api<Account[]>('/payments/accounts'),
        api<FeePlan[]>('/payments/fee-plans'),
      ]);
      setProviders(p); setAccounts(a); setFeePlans(f);
      api<Tier[]>('/subscription-tiers').then(setTiers).catch(() => {});
      api<BusinessSetting[]>('/invoices/business-settings').then(setSettings).catch(() => {});
    } catch (e: any) {
      toast.error(e.message);
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      {/* ── Accounts ─────────────────────────────────────────────── */}
      <section className={card}>
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Gateway accounts</h3>
            <p className="text-sm text-gray-500">
              A Gympe account serves many gyms: it either collects and pays each gym out to its linked account (split), or, as a partner
              (PhonePe), settles straight to each gym's own merchant ID. Gym accounts are a gym's own merchant account.
              Which account each gym uses, and its ID there, is set on the gym's Payments tab.
            </p>
          </div>
          <button onClick={() => setAdding(v => !v)} className="shrink-0 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium">
            {adding ? 'Close' : 'Add account'}
          </button>
        </div>

        {adding && <AddAccountForm providers={providers} api={api} onDone={() => { setAdding(false); load(); }} />}

        <div className="divide-y divide-gray-100">
          {accounts.length === 0 && <p className="text-sm text-gray-500 py-3">No accounts yet.</p>}
          {accounts.map(a => (
            <AccountRow key={a.id} account={a} provider={providers.find(p => p.key === a.provider)} api={api} onChange={load} />
          ))}
        </div>
      </section>

      {/* ── Fee plans ────────────────────────────────────────────── */}
      <section className={card}>
        <h3 className="text-base font-semibold text-gray-900">Fee plans</h3>
        <p className="text-sm text-gray-500 mb-4">
          Gympe's fee on each online payment (before GST). A gym uses its own override, otherwise its tier's plan, otherwise no fee.
          Fees are billed on Gympe's monthly invoice, which appears in the gym's expenses.
        </p>
        <FeePlans plans={feePlans} api={api} onChange={load} />
        {tiers.length > 0 && (
          <div className="mt-5">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Tier defaults</h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {tiers.map(t => (
                <label key={t.id} className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-gray-700">{t.name}</span>
                  <select
                    className="rounded-lg border-gray-300 text-sm"
                    value={t.feePlanId ?? ''}
                    onChange={async e => {
                      try {
                        await api(`/payments/tiers/${t.id}/fee-plan`, { method: 'PUT', body: JSON.stringify({ feePlanId: e.target.value || null }) });
                        toast.success(`${t.name} fee plan updated`);
                        load();
                      } catch (err: any) { toast.error(err.message); }
                    }}
                  >
                    <option value="">No fee</option>
                    {feePlans.filter(p => p.isActive).map(p => <option key={p.id} value={p.id}>{p.name} · {feeLabel(p)}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Fee invoicing ────────────────────────────────────────── */}
      <FeeInvoicing settings={settings} api={api} />
    </div>
  );
}

function AddAccountForm({ providers, api, onDone }: { providers: Provider[]; api: ReturnType<typeof useSaasApi>; onDone: () => void }) {
  const [chosenKey, setProviderKey] = useState('');
  // The form may open before the provider list arrives; default to its first.
  const providerKey = chosenKey || providers[0]?.key || '';
  const [owner, setOwner] = useState<'platform' | 'gym'>('platform');
  const [ownerGymId, setOwnerGymId] = useState('');
  const [label, setLabel] = useState('');
  const [environment, setEnvironment] = useState<'test' | 'live'>('test');
  const [settlement, setSettlement] = useState<'split' | 'direct'>('split');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<Record<string, string>>({});
  const [webhookSecret, setWebhookSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const provider = providers.find(p => p.key === providerKey);
  const fields = provider?.credentialFields ?? [];
  const canSplit = !!provider?.capabilities.split;

  const save = async () => {
    setSaving(true);
    try {
      await api('/payments/accounts', {
        method: 'POST',
        body: JSON.stringify({
          provider: providerKey,
          owner,
          ownerGymId: owner === 'gym' ? ownerGymId : undefined,
          // A gym's own account always settles to the gym; the server decides.
          settlement: owner === 'platform' ? (canSplit ? settlement : 'direct') : undefined,
          label,
          environment,
          credentials,
          config: cleanConfig(config),
          webhookSecret: !provider?.webhooksManaged && webhookSecret ? webhookSecret : undefined,
        }),
      });
      toast.success(provider?.webhooksManaged ? 'Account added. Register its webhook from the account list.' : 'Account added. Configure its webhook with the URL shown.');
      onDone();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-5 grid sm:grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
      <label className="text-sm">Gateway
        <select className={input} value={providerKey} onChange={e => { setProviderKey(e.target.value); setCredentials({}); setConfig({}); }}>
          {providers.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </label>
      <label className="text-sm">Owner
        <select className={input} value={owner} onChange={e => setOwner(e.target.value as any)}>
          <option value="platform">Gympe (used for many gyms)</option>
          <option value="gym">A gym's own account (direct)</option>
        </select>
      </label>
      {owner === 'gym' && (
        <label className="text-sm sm:col-span-2">Gym ID
          <input className={input} value={ownerGymId} onChange={e => setOwnerGymId(e.target.value.trim())} placeholder="Gym UUID" />
        </label>
      )}
      {owner === 'platform' && (canSplit ? (
        <label className="text-sm sm:col-span-2">Settlement
          <select className={input} value={settlement} onChange={e => setSettlement(e.target.value as 'split' | 'direct')}>
            <option value="split">Gympe collects, pays out to each gym's linked account (split)</option>
            <option value="direct">Settles straight to each gym's own merchant ID (partner)</option>
          </select>
        </label>
      ) : (
        <p className="text-xs text-gray-500 sm:col-span-2">
          {provider?.label ?? 'This gateway'} can't split a payment, so a Gympe account settles straight to each gym's own merchant ID
          (set per gym on its Payments tab).
        </p>
      ))}
      <label className="text-sm">Label
        <input className={input} value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Gympe Razorpay (live)" />
      </label>
      <label className="text-sm">Environment
        <select className={input} value={environment} onChange={e => setEnvironment(e.target.value as any)}>
          <option value="test">Test</option>
          <option value="live">Live</option>
        </select>
      </label>
      {fields.map(f => (
        <label key={f.key} className="text-sm">{f.label}
          <input className={input} type={f.secret ? 'password' : 'text'} autoComplete="off"
            value={credentials[f.key] ?? ''} onChange={e => setCredentials(c => ({ ...c, [f.key]: e.target.value.trim() }))} />
        </label>
      ))}
      <ConfigInputs fields={provider?.configFields ?? []} values={config} onChange={setConfig} />
      {provider?.webhooksManaged ? (
        <p className="text-xs text-gray-500 sm:col-span-2">
          {provider.label} webhooks are registered over its API: save the account, then register its webhook from the account list.
          Gympe generates the credentials.
        </p>
      ) : (
        <label className="text-sm">Webhook secret
          <input className={input} type="password" autoComplete="off" value={webhookSecret} onChange={e => setWebhookSecret(e.target.value.trim())} />
        </label>
      )}
      <div className="sm:col-span-2 flex justify-end">
        <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">
          {saving ? 'Saving…' : 'Save account'}
        </button>
      </div>
    </div>
  );
}

/** A provider's config fields: a blank one uses its fallback, which the placeholder names. */
function ConfigInputs({ fields, values, onChange }: {
  fields: ConfigField[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  return (
    <>
      {fields.map(f => (
        <label key={f.key} className="text-sm">{f.label}{f.required ? '' : ' (optional)'}
          <input className={input} autoComplete="off" value={values[f.key] ?? ''}
            placeholder={f.fallback ? `Blank uses ${f.fallback}` : undefined}
            onChange={e => onChange({ ...values, [f.key]: e.target.value })} />
        </label>
      ))}
    </>
  );
}

function AccountRow({ account: a, provider, api, onChange }: {
  account: Account;
  provider?: Provider;
  api: ReturnType<typeof useSaasApi>;
  onChange: () => void;
}) {
  const [open, setOpen] = useState<'settings' | 'webhook' | null>(null);
  const webhookUrl = `${API_BASE_URL}${a.webhookPath}`;
  const kind = accountKind(a);
  const canChangeSettlement = a.owner === 'platform' && !!provider?.capabilities.split;
  const hasSettings = a.configFields.length > 0 || canChangeSettlement;
  const update = async (body: object, done: string) => {
    try {
      await api(`/payments/accounts/${a.id}`, { method: 'PUT', body: JSON.stringify(body) });
      toast.success(done);
      onChange();
      return true;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };
  const toggle = (panel: 'settings' | 'webhook') => setOpen(o => (o === panel ? null : panel));

  return (
    <div data-testid="account-row" className="py-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{a.label}</span>
            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{a.providerLabel}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${kind.tone}`}>{kind.badge}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${a.environment === 'live' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>{a.environment}</span>
            {a.status === 'disabled' && <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">disabled</span>}
            {!a.hasWebhookSecret && (
              <span className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700">{a.webhooksManaged ? 'webhook not registered' : 'no webhook secret'}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 truncate">
            {Object.entries(a.credentials).map(([k, v]) => `${k}: ${v}`).join(' · ')}{a.ownerGymId ? ` · gym ${a.ownerGymId}` : ''}
          </p>
          {a.configFields.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {a.configFields.map(f => `${f.label}: ${String(a.config?.[f.key] ?? '') || f.fallback || 'not set'}`).join(' · ')}
            </p>
          )}
          {!a.webhooksManaged && (
            <button className="text-xs text-indigo-600 mt-0.5 truncate max-w-full text-left" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Webhook URL copied'); }}>
              Webhook: {webhookUrl}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {hasSettings && (
            <button className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium" onClick={() => toggle('settings')}>Settings</button>
          )}
          {a.webhooksManaged ? (
            <button className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium" onClick={() => toggle('webhook')}>Webhook</button>
          ) : (
            <button className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium"
              onClick={() => {
                const secret = window.prompt('New webhook secret (write-only):');
                if (secret) update({ webhookSecret: secret.trim() }, 'Webhook secret updated');
              }}>Set webhook secret</button>
          )}
          <button className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium"
            onClick={() => update({ status: a.status === 'active' ? 'disabled' : 'active' }, a.status === 'active' ? 'Account disabled' : 'Account enabled')}>
            {a.status === 'active' ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
      {open === 'settings' && (
        <AccountSettings account={a} canChangeSettlement={canChangeSettlement}
          onSave={async body => { if (await update(body, 'Account settings saved')) setOpen(null); }} />
      )}
      {open === 'webhook' && <WebhookPanel account={a} api={api} onChange={onChange} />}
    </div>
  );
}

/** An account's provider config and, for a split-capable Gympe account, its settlement. */
function AccountSettings({ account: a, canChangeSettlement, onSave }: {
  account: Account;
  canChangeSettlement: boolean;
  onSave: (body: { config: Record<string, unknown>; settlement?: 'direct' | 'split' }) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(a.configFields.map(f => [f.key, String(a.config?.[f.key] ?? '')])));
  const [settlement, setSettlement] = useState(a.settlement);

  const save = () => {
    if (settlement !== a.settlement && !window.confirm(
      `Change how every gym on "${a.label}" is paid? ${settlement === 'split'
        ? 'Gympe will collect and pay each gym out to its linked account.'
        : "Payments will settle straight to each gym's own merchant ID."}`,
    )) return;
    // The stored config is replaced whole: keep keys the form doesn't show.
    const own = new Set(a.configFields.map(f => f.key));
    const kept = Object.fromEntries(Object.entries(a.config ?? {}).filter(([k]) => !own.has(k)));
    onSave({ config: { ...cleanConfig(values), ...kept }, ...(settlement !== a.settlement && { settlement }) });
  };

  return (
    <div className="mt-3 grid sm:grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
      <ConfigInputs fields={a.configFields} values={values} onChange={setValues} />
      {canChangeSettlement && (
        <label className="text-sm sm:col-span-2">Settlement
          <select className={input} value={settlement} onChange={e => setSettlement(e.target.value as 'split' | 'direct')}>
            <option value="split">Gympe collects, pays out to each gym's linked account (split)</option>
            <option value="direct">Settles straight to each gym's own merchant ID (partner)</option>
          </select>
        </label>
      )}
      <div className="sm:col-span-2 flex justify-end">
        <button onClick={save} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium">Save settings</button>
      </div>
    </div>
  );
}

/**
 * Webhook registration over the gateway's API (PhonePe). Registering generates
 * a basic-auth pair, registers this deployment's URL with the gateway and keeps
 * the pair on the account once accepted. The password is shown in that one
 * response and nowhere else.
 */
function WebhookPanel({ account: a, api, onChange }: { account: Account; api: ReturnType<typeof useSaasApi>; onChange: () => void }) {
  const [status, setStatus] = useState<WebhookStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [registered, setRegistered] = useState<Registered | null>(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    setError(null);
    try {
      setStatus(await api<WebhookStatus>(`/payments/accounts/${a.id}/webhooks`));
    } catch (e: any) {
      setError(e.message);
    }
  }, [api, a.id]);
  useEffect(() => { check(); }, [check]);

  const register = async () => {
    if (a.hasWebhookSecret && !window.confirm(
      `Register again? ${a.providerLabel} will be given new credentials; a registration made with the old ones fails verification until removed.`,
    )) return;
    setBusy(true);
    try {
      setRegistered(await api<Registered>(`/payments/accounts/${a.id}/webhook`, { method: 'POST' }));
      toast.success('Webhook registered');
      onChange();
      check();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string, url: string) => {
    if (!window.confirm(`Remove the ${a.providerLabel} webhook to ${url}?`)) return;
    try {
      await api(`/payments/accounts/${a.id}/webhooks/${encodeURIComponent(id)}`, { method: 'DELETE' });
      toast.success('Webhook removed');
      check();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="mt-3 bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
      {error && <p className="text-rose-700">Couldn't ask {a.providerLabel}: {error}</p>}
      {!status && !error && <p className="text-gray-500">Asking {a.providerLabel}…</p>}
      {status && (
        <>
          {status.expectedUrl ? (
            <div>
              <p className={status.registered ? 'text-emerald-700 font-medium' : 'text-rose-700 font-medium'}>
                {status.registered ? `${a.providerLabel} sends to this deployment.` : 'Not pointed at this deployment.'}
              </p>
              <p className="text-xs text-gray-500 break-all">{status.expectedUrl}</p>
            </div>
          ) : (
            <p className="text-rose-700">PUBLIC_API_URL is not set on the API, so there is no address to register.</p>
          )}
          {status.webhooks.length > 0 && (
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white">
              {status.webhooks.map(w => (
                <li key={w.id} className="flex items-center gap-3 px-3 py-2">
                  <span className="flex-1 min-w-0 text-xs text-gray-700 break-all">{w.url}</span>
                  {!w.enabled && <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">disabled</span>}
                  <button className="text-xs text-rose-600 shrink-0" onClick={() => remove(w.id, w.url)}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {registered && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="font-medium text-amber-900">Registered. These credentials are shown once — keep them if you need them.</p>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-gray-500">Username</dt><dd className="font-mono break-all">{registered.username}</dd>
            <dt className="text-gray-500">Password</dt><dd className="font-mono break-all">{registered.password}</dd>
            <dt className="text-gray-500">Events</dt><dd>{registered.events.join(', ')}</dd>
          </dl>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={check} className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium">Check again</button>
        <button onClick={register} disabled={busy || (!!status && !status.expectedUrl)}
          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium disabled:opacity-50">
          {busy ? 'Registering…' : 'Register webhook'}
        </button>
      </div>
    </div>
  );
}

function FeePlans({ plans, api, onChange }: { plans: FeePlan[]; api: ReturnType<typeof useSaasApi>; onChange: () => void }) {
  const [name, setName] = useState('');
  const [percent, setPercent] = useState('');
  const [fixed, setFixed] = useState('');
  const create = async () => {
    try {
      await api('/payments/fee-plans', {
        method: 'POST',
        body: JSON.stringify({
          name,
          percentBps: Math.round(Number(percent || 0) * 100),
          fixedPaise: Math.round(Number(fixed || 0) * 100),
        }),
      });
      setName(''); setPercent(''); setFixed('');
      toast.success('Fee plan created');
      onChange();
    } catch (e: any) { toast.error(e.message); }
  };
  return (
    <div className="space-y-3">
      <div className="divide-y divide-gray-100">
        {plans.map(p => (
          <div key={p.id} className="py-2 flex items-center justify-between gap-4">
            <span className={`text-sm ${p.isActive ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{p.name}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 tabular-nums">{feeLabel(p)}</span>
              <button className="text-xs text-indigo-600" onClick={async () => {
                try {
                  await api(`/payments/fee-plans/${p.id}`, { method: 'PUT', body: JSON.stringify({ isActive: !p.isActive }) });
                  onChange();
                } catch (e: any) { toast.error(e.message); }
              }}>{p.isActive ? 'Retire' : 'Restore'}</button>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_auto] gap-2 items-end">
        <label className="text-sm">Name<input className={input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Standard 2%" /></label>
        <label className="text-sm">Percent<input className={input} inputMode="decimal" value={percent} onChange={e => setPercent(e.target.value)} placeholder="2.00" /></label>
        <label className="text-sm">Fixed ₹<input className={input} inputMode="decimal" value={fixed} onChange={e => setFixed(e.target.value)} placeholder="0" /></label>
        <button onClick={create} disabled={!name.trim()} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">Add plan</button>
      </div>
    </div>
  );
}

function FeeInvoicing({ settings, api }: { settings: BusinessSetting[]; api: ReturnType<typeof useSaasApi> }) {
  const firstOfMonth = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; })();
  const [periodEnd, setPeriodEnd] = useState(firstOfMonth);
  const [settingId, setSettingId] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<Array<{ gymId: string; invoiceNumber: string; charges: number; feeTotal: number }> | null>(null);

  useEffect(() => { if (!settingId && settings[0]) setSettingId(settings[0].id); }, [settings, settingId]);

  const run = async () => {
    if (!window.confirm(`Invoice every fee accrued before ${periodEnd}? Each gym gets one invoice; it also appears in the gym's expenses.`)) return;
    setRunning(true);
    try {
      const r = await api<typeof results>('/payments/fee-invoices', { method: 'POST', body: JSON.stringify({ businessSettingId: settingId, periodEnd }) });
      setResults(r);
      toast.success(r && r.length ? `${r.length} invoice${r.length > 1 ? 's' : ''} issued` : 'Nothing to invoice');
    } catch (e: any) { toast.error(e.message); } finally { setRunning(false); }
  };

  return (
    <section className={card}>
      <h3 className="text-base font-semibold text-gray-900">Fee invoicing</h3>
      <p className="text-sm text-gray-500 mb-4">Bills fees accrued before the chosen date (exclusive). Safe to rerun — a fee is never billed twice.</p>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2 items-end">
        <label className="text-sm">Issue from
          <select className={input} value={settingId} onChange={e => setSettingId(e.target.value)}>
            {settings.map(s => <option key={s.id} value={s.id}>{s.businessName}</option>)}
          </select>
        </label>
        <label className="text-sm">Up to (exclusive)<input type="date" className={input} value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} /></label>
        <button onClick={run} disabled={running || !settingId} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">
          {running ? 'Invoicing…' : 'Invoice accrued fees'}
        </button>
      </div>
      {results && results.length > 0 && (
        <ul className="mt-3 text-sm text-gray-700 space-y-1">
          {results.map(r => <li key={r.invoiceNumber}>{r.invoiceNumber} — gym {r.gymId.slice(0, 8)} · {r.charges} payments · ₹{r.feeTotal.toFixed(2)} + GST</li>)}
        </ul>
      )}
    </section>
  );
}
